// src/App.js
import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import Calendar from './components/Calendar/Calendar';
import EventList from './components/EventList/EventList';
import Navbar from './components/Navbar/Navbar';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { api } from './services/api';
import './App.css';

// Lazy load the EventDetail component
const EventDetail = React.lazy(() => import('./pages/EventDetail/EventDetail'));

const LoadingFallback = () => (
  <div className="loading">Loading...</div>
);

const AboutSection = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="about-section">
      <button 
        onClick={() => setIsExpanded(!isExpanded)}
        className="about-button"
      >
        About
      </button>
      {isExpanded && (
        <div className="about-content">
          <p>The Celebrating Diversity - Calendar Application provides a comprehensive view of important dates, events, and observances relevant to the YorkU community. This includes cultural celebrations, and community events. The calendar feature will enable users to explore cultural festivals, holidays and significant events, deepening knowledge and appreciation of global history and diverse practices. This project is funded by the Equity, Diversity and Inclusion seed funding by the Lassonde School of Engineering.</p>
        </div>
      )}
    </div>
  );
};

const AcknowledgementSection = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="acknowledgement-section">
      <button 
        onClick={() => setIsExpanded(!isExpanded)}
        className="acknowledgement-button"
      >
        Acknowledgement
      </button>
      {isExpanded && (
        <div className="acknowledgement-content">
          <p>This work is part of the project funded by Lassonde School of Engineering – Research - EDI Seed Fund competition in Fall 2023-24. The award is provided to the projects that support Lassonde's Strategic Academic Plan and Equity, Diversity, and Inclusion (EDI). The project title is "Celebrating Diversity Calendar Application". This innovative project presents an opportunity to create a platform that not only educates and informs but also celebrates the rich tapestry of cultures, traditions, and celebrations worldwide. Moreover, this idea promotes diverse communities at the ground level breaking free from year-round celebrations that are based on colonization traditions alone. The work was also mentioned in the<a href='https://lassonde.yorku.ca/impactreport2024/'> Lassonde's 2023-24 Annual Impact Report </a></p>
        </div>
      )}
    </div>
  );
};

const CalendarView = ({ 
  selectedDate, 
  monthEvents, 
  selectedDateEvents, 
  loading, 
  error, 
  handleDateChange 
}) => (
  <>
    <div className="app-description">
      Calendar showing important dates and events for the YorkU community.
    </div>
    {error && <div className="error-message">{error}</div>}
    {loading ? (
      <div className="loading">Loading events...</div>
    ) : (
      <div className="calendar-layout">
        <div className="calendar-section">
          <Calendar
            value={selectedDate || new Date()}
            onChange={handleDateChange}
            events={monthEvents || []}
          />
          <div className="info-sections">
            <div className="buttons-container">
              <AboutSection />
              <AcknowledgementSection />
            </div>
          </div>
        </div>
        <div className="events-section">
          <EventList
            events={selectedDateEvents || []}
            selectedDate={selectedDate || new Date()}
            loading={loading}
          />
        </div>
      </div>
    )}
  </>
);

const App = () => {
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [monthEvents, setMonthEvents] = useState([]);
  const [selectedDateEvents, setSelectedDateEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAPI = async () => {
      try {
        const status = await api.checkAPIStatus();
        if (!status) {
          setError('Unable to connect to the API');
        }
      } catch (err) {
        setError('Unable to connect to the API');
      }
    };
    checkAPI();
  }, []);

  useEffect(() => {
    const fetchMonthEvents = async () => {
      if (!selectedDate) return;
      
      setLoading(true);
      try {
        const year = selectedDate.getFullYear();
        const month = selectedDate.getMonth() + 1;
        const events = await api.getEventsByMonth(year, month);
        setMonthEvents(events || []);
        setError(null);
      } catch (err) {
        console.error('Error loading month events:', err);
        setError('Failed to load events');
        setMonthEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMonthEvents();
  }, [selectedDate]);

  useEffect(() => {
    const fetchDateEvents = async () => {
      if (!selectedDate) return;

      try {
        const events = await api.getEventsByDate(selectedDate);
        setSelectedDateEvents(events || []);
      } catch (err) {
        console.error('Error loading date events:', err);
        setSelectedDateEvents([]);
      }
    };

    fetchDateEvents();
  }, [selectedDate]);

  const handleDateChange = (date) => {
    if (date && date instanceof Date && !isNaN(date)) {
      setSelectedDate(date);
    }
  };

  return (
    <div className="app">
      <Navbar />
      <main className="main-content">
        <Routes>
          <Route 
            path="/" 
            element={
              <CalendarView
                selectedDate={selectedDate}
                monthEvents={monthEvents}
                selectedDateEvents={selectedDateEvents}
                loading={loading}
                error={error}
                handleDateChange={handleDateChange}
              />
            } 
          />
          <Route 
            path="/event/:id" 
            element={
              <Suspense fallback={<LoadingFallback />}>
                <EventDetail />
              </Suspense>
            } 
          />
        </Routes>
      </main>
    </div>
  );
};

export default App;
